import { useInfiniteQuery, useQuery, useQueryClient } from "@tanstack/react-query";
import { addDays, addSeconds } from "date-fns";
import { usePostHog } from "posthog-js/react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Link } from "react-router";
import { twJoin } from "tailwind-merge";

import type { AdminNotificationDto } from "@/api/types";
import { ActivityDot } from "@/components/ActivityDot/ActivityDot";
import { Breadcrumbs } from "@/components/Breadcrumbs/Breadcrumbs";
import { ConfirmModal } from "@/components/ConfirmModal/ConfirmModal";
import { ErrorPage } from "@/components/Error/ErrorPage";
import { FormattedDate } from "@/components/FormattedDate/FormattedDate";
import { ArrowNarrowUpIcon } from "@/components/Icons/ArrowNarrowUpIcon";
import { CheckIcon } from "@/components/Icons/CheckIcon";
import { DocumentPage } from "@/components/Page/DocumentPage";
import { Box } from "@/design-system/Box";
import { Button } from "@/design-system/Button";
import { Card } from "@/design-system/Card";
import { CheckboxMultiSelect } from "@/design-system/CheckboxMultiSelect";
import { CollectionLoadingState } from "@/design-system/CollectionState";
import { FullSizeLoader } from "@/design-system/FullSizeLoader";
import { Inline } from "@/design-system/Inline";
import { Switch } from "@/design-system/Switch";
import { resolveOldLink } from "@/helpers/old-url-resolver";
import { LOCAL_STORAGE_KEYS } from "@/helpers/storage";
import { useProjectId } from "@/hooks/Network/useProjectId";
import { useSessionUser } from "@/hooks/Network/useSessionUser";
import { useBool } from "@/hooks/useBool";
import { useCurrentDate } from "@/hooks/useCurrentDate";
import { getSessionStorageValue, useUpdateSessionStorage } from "@/hooks/useLocalStorage";
import { useOnIntersection } from "@/hooks/useOnIntersection";
import { usePermission } from "@/hooks/usePermission";
import { useScrollToPageTop } from "@/hooks/useScrollToPageTop";
import { useSlug } from "@/hooks/useSlug";
import { NotificationIcon } from "@/modules/admin-notifications/components/NotificationIcon";
import { adminNotificationMutations } from "@/queries/notifications/mutations";
import { useNotificationQueries } from "@/queries/notifications/queryOptions";
import { useUserQueries } from "@/queries/users/queryOptions";
import { QUERY_KEYS } from "@/query-keys";
import { routes } from "@/routes";
import { useTranslation } from "@/translations";
import type { ApiQueryParams } from "@/types/api-types";

import { canEditOwnNotificationSettings } from "../../permissions";

interface Filters {
  unreadOnly: boolean;
  types: Exclude<ApiQueryParams<"getAdminNotificationsV1">["Types"], undefined>;
}
export function AdminNotificationsPage(): React.ReactNode {
  const clockDate = useCurrentDate();
  const slug = useSlug();
  const projectId = useProjectId();
  const { t } = useTranslation();
  const sessionUser = useSessionUser();
  const [isMarkAsReadModalOpen, markAsReadModalHandlers] = useBool();
  const hasPermission = usePermission();
  const postHog = usePostHog();
  const queryClient = useQueryClient();
  const userQueries = useUserQueries();
  const notificationQueries = useNotificationQueries();
  const { data: notificationSettings } = useQuery(userQueries.notificationSettings({ userId: sessionUser.id }));
  const options = useMemo(
    () =>
      [
        {
          label: "Messages",
          type: "message",
          disabled: !notificationSettings?.generalMessageNew.dashboardEnabled,
        },
        {
          label: "Message Comments",
          type: "messageComment",
          disabled: !notificationSettings?.generalMessageCommentNew.dashboardEnabled,
        },
        {
          label: "Messages in Groups",
          type: "groupMessage",
          disabled: false,
        },
        {
          label: "Tickets",
          type: "ticket",
          disabled: !notificationSettings?.ticketUnassignedUpdates.some((x) => x.dashboardEnabled),
        },
        {
          label: "Tickets Assigned",
          type: "ticketAssignee",
          disabled: !notificationSettings?.ticketAssignToMe.dashboardEnabled,
        },
        {
          label: "Ticket Comments",
          type: "ticketComment",
          disabled: !notificationSettings?.ticketAssignedCommentNew.dashboardEnabled,
        },
      ] as const,
    [
      notificationSettings?.generalMessageCommentNew.dashboardEnabled,
      notificationSettings?.generalMessageNew.dashboardEnabled,
      notificationSettings?.ticketAssignToMe.dashboardEnabled,
      notificationSettings?.ticketAssignedCommentNew.dashboardEnabled,
      notificationSettings?.ticketUnassignedUpdates,
    ],
  );
  const [filters, setFilters] = useState<Filters>(() => {
    const storage = getSessionStorageValue<Filters | undefined>(
      LOCAL_STORAGE_KEYS.ADMIN_NOTIFICATION_FILTERS,
      undefined,
    );

    return {
      unreadOnly: storage?.unreadOnly ?? false,
      types: storage?.types ?? [],
    };
  });
  useUpdateSessionStorage(LOCAL_STORAGE_KEYS.ADMIN_NOTIFICATION_FILTERS, filters);

  const query = {
    ExcludeRead: filters.unreadOnly,
    Types: filters.types,
  } satisfies ApiQueryParams<"getAdminNotificationsV1">;
  const {
    data,
    isPending,
    error,
    hasNextPage,
    fetchNextPage,
    isFetchingNextPage,
    dataUpdatedAt: listUpdatedAt,
  } = useInfiniteQuery(notificationQueries.adminListInfinite(query));
  const markReadMutation = adminNotificationMutations.useMarkNotificationsAsRead(data, query);
  const markAllAsReadMutation = adminNotificationMutations.useMarkAllNotificationAsRead(data, query);
  const allItems = data?.pages.flatMap((page) => page.items) ?? [];
  const sevenDaysAgo = addDays(clockDate, -7);
  const itemsFromThisWeek = allItems.filter((x) => new Date(x.sentAt) > sevenDaysAgo);
  const itemsFromBefore = allItems.slice(itemsFromThisWeek.length);
  const ref = useOnIntersection({
    threshold: 0,
    onIntersect: useCallback(() => {
      if (!isFetchingNextPage && hasNextPage) {
        void fetchNextPage();
      }
    }, [fetchNextPage, hasNextPage, isFetchingNextPage]),
  });
  const { data: adminNotificationStats, dataUpdatedAt: statusUpdatedAt } = useQuery(notificationQueries.adminStatus());
  const scrollToTop = useScrollToPageTop();
  const lastAdminNotificationStats = useRef<number | null>(null);
  const [showPill, showPillHandlers] = useBool();
  useEffect(() => {
    if (!adminNotificationStats) {
      return;
    }
    if (
      statusUpdatedAt &&
      listUpdatedAt &&
      addSeconds(new Date(statusUpdatedAt), -2) > new Date(listUpdatedAt) &&
      lastAdminNotificationStats.current != null &&
      lastAdminNotificationStats.current < adminNotificationStats.amount
    ) {
      showPillHandlers.setTrue();
    }
    lastAdminNotificationStats.current = adminNotificationStats.amount;
  }, [adminNotificationStats, listUpdatedAt, showPillHandlers, statusUpdatedAt]);
  const onClickMarkAllAsRead = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    markAsReadModalHandlers.setTrue();
    postHog.capture("clicked_maar_admin_notifications");
  };
  if (error) {
    return <ErrorPage error={error} />;
  }

  return (
    <>
      <DocumentPage
        containerBoundary="inherit"
        tabTitle={t("page.admin-notifications.title.tab")}
        breadcrumbs={
          <Breadcrumbs
            pages={[
              {
                name: sessionUser.project.name,
              },
              {
                name: t("page.admin-notifications.title"),
              },
            ]}
          />
        }
        header={
          <Inline gap="md" justify="between">
            <div className="w-full max-w-search-compact">
              <CheckboxMultiSelect
                renderMoreSelected={(count) => t("common.label.x-more", { count })}
                placeholder={t("page.admin-notifications.filters.types.placeholder")}
                items={options}
                keySelector={(x) => x.type}
                renderOption={(x) => x.label}
                disabledItems={options.filter((x) => x.disabled)}
                disabledItemsStillSelectable
                selected={options.filter((o) => filters.types.includes(o.type))}
                onChange={(x) =>
                  setFilters((oldState) => ({
                    ...oldState,
                    types: x.map((y) => y.type),
                  }))
                }
              />
            </div>

            <Inline gap="sm" align="center">
              <Inline gap="sm" align="center">
                <span>{t("page.admin-notifications.filters.unread-only")}</span>
                <Switch
                  isChecked={filters.unreadOnly}
                  onChange={() =>
                    setFilters((oldState) => ({
                      ...oldState,
                      unreadOnly: !filters.unreadOnly,
                    }))
                  }
                  ariaLabel={t("page.admin-notifications.filters.unread-only")}
                />
              </Inline>
              <Button onClick={onClickMarkAllAsRead} variant="soft" icon={<CheckIcon />}>
                {t("page.admin-notifications.mark-all-as-read")}
              </Button>
            </Inline>
          </Inline>
        }
        actions={
          hasPermission(canEditOwnNotificationSettings) && (
            <Button
              type="link"
              href={routes.adminNotifications.selfSettings({
                slug,
              })}
              state={{
                fromPage: routes.adminNotifications.list({
                  slug,
                }),
              }}
            >
              {t("page.admin-notifications.settings")}
            </Button>
          )
        }
      >
        {showPill &&
          createPortal(
            <aside className="fixed top-1/2 right-8 z-0 w-fit -translate-y-1/2">
              <Button
                onClick={() => {
                  scrollToTop();
                  showPillHandlers.setFalse();
                  void queryClient.invalidateQueries({
                    queryKey: QUERY_KEYS.ADMIN_NOTIFICATION_LIST(projectId, query),
                  });
                }}
                icon={<ArrowNarrowUpIcon />}
                isCircular
              >
                {t("page.admin-notifications.new-notifications")}
              </Button>
            </aside>,
            document.body,
          )}
        <NotificationList
          isPending={isPending}
          allItems={allItems}
          itemsFromThisWeek={itemsFromThisWeek}
          itemsFromBefore={itemsFromBefore}
          slug={slug}
          onMarkAsRead={(id) => markReadMutation.mutate(id)}
        />
        {hasNextPage && <CollectionLoadingState ref={ref} />}
      </DocumentPage>
      <ConfirmModal
        title={t("page.admin-notifications.modal.mark-all-as-read.title")}
        description={t("page.admin-notifications.modal.mark-all-as-read.description")}
        isLoading={markAllAsReadMutation.isPending}
        tone="primary"
        onOpenChange={markAsReadModalHandlers.set}
        onReject={(e) => {
          e.preventDefault();
          e.stopPropagation();
          markAsReadModalHandlers.setFalse();
        }}
        rejectBtnProps={{
          "data-testid": "notification-mark-all-as-read-modal-cancel",
        }}
        onResolve={() => {
          markAllAsReadMutation.mutate();
          markAsReadModalHandlers.setFalse();
        }}
        resolveBtnProps={{
          text: t("page.admin-notifications.modal.mark-all-as-read.confirm"),
          "data-testid": "notification-mark-all-as-read-modal-confirm",
        }}
        isOpened={isMarkAsReadModalOpen}
        shouldCloseOnEsc
      />
    </>
  );
}
function NotificationList({
  isPending,
  allItems,
  itemsFromThisWeek,
  itemsFromBefore,
  slug,
  onMarkAsRead,
}: {
  isPending: boolean;
  allItems: AdminNotificationDto[];
  itemsFromThisWeek: AdminNotificationDto[];
  itemsFromBefore: AdminNotificationDto[];
  slug: string;
  onMarkAsRead: (id: string) => void;
}): React.ReactNode {
  const { t } = useTranslation();

  if (isPending) {
    return <FullSizeLoader withPadding />;
  }
  if (allItems.length === 0) {
    return (
      <Card as="section" density="flush">
        <Box padding="xl">
          <h2 className="text-heading-3 text-text-secondary">{t("page.admin-notifications.empty.title")}</h2>
          <p className="text-heading-4 text-text-secondary">{t("page.admin-notifications.empty.description")}</p>
        </Box>
      </Card>
    );
  }

  return (
    <>
      {itemsFromThisWeek.length > 0 ? (
        <section>
          <h2 className="rounded-t-lg bg-background-default py-space-md pl-space-xl text-body-emphasis">
            {t("page.admin-notifications.this-week")}
          </h2>
          {itemsFromThisWeek.map((x) => (
            <NotificationItem key={x.id} notification={x} slug={slug} onMarkAsRead={() => onMarkAsRead(x.id)} />
          ))}
        </section>
      ) : null}
      {itemsFromBefore.length > 0 ? (
        <section>
          <h2
            className={twJoin(
              "bg-background-default py-space-md pl-space-xl text-body-emphasis",
              itemsFromThisWeek.length === 0 ? "rounded-t-lg" : "",
            )}
          >
            {t("page.admin-notifications.before-this-week")}
          </h2>
          {itemsFromBefore.map((x) => (
            <NotificationItem key={x.id} notification={x} slug={slug} onMarkAsRead={() => onMarkAsRead(x.id)} />
          ))}
        </section>
      ) : null}
    </>
  );
}
function NotificationItem({
  notification,
  slug,
  onMarkAsRead,
}: {
  notification: AdminNotificationDto;
  slug: string;
  onMarkAsRead: () => void;
}) {
  return (
    <Link
      to={resolveOldLink(notification.buttonUrl, slug)}
      className={twJoin(
        "group flex items-center justify-start bg-background-default p-card-compact even:bg-background-secondary",
      )}
      onClick={() => {
        if (!notification.isRead) {
          onMarkAsRead();
        }
      }}
    >
      <span className="mr-space-sm flex w-icon-inline py-space-md">{!notification.isRead && <ActivityDot />}</span>
      <span className="rounded-full border border-border-default bg-background-default p-space-sm text-text-secondary transition-colors">
        <NotificationIcon type={notification.type} />
      </span>
      <section className="flex flex-col items-start justify-start gap-space-xs px-space-sm">
        <span
          className="line-clamp-1 text-left text-caption whitespace-pre-wrap text-text-primary group-hocus:underline [&>b]:text-caption-emphasis"
          dangerouslySetInnerHTML={{
            __html: notification.message,
          }}
        />

        <span className="text-overline text-text-secondary">
          <FormattedDate format="datetimeRelative" date={notification.sentAt} />
        </span>
      </section>
    </Link>
  );
}
